import theme from "assets/theme";
import React, { useContext, useMemo } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import TooltipText from "webComponentsFormElements/TooltipText.component";
import ClickStyledText from "webComponentsShared/ClickStyledText.component";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  title: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    paddingLeft: 8,
    paddingBottom: 8,
    paddingTop: 8,
  },
  scrollContainer: {
    maxHeight: 150,
  },
  headers: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    paddingLeft: 16,
    paddingBottom: 8,
  },
  content: {
    fontWeight: theme.font.weight.regular,
    color: theme.color.darkGrey,
    paddingLeft: 24,
    paddingBottom: 8,
  },
  noneText: {
    fontStyle: "italic",
  },
});

export default function AlertRecipientSummary(props) {
  const { onPress, teamIds, locationIds, userIds } = props;

  const {
    teamList,
    personList,
    locationList,
    personsByTeam,
    personsByLocation,
  } = useContext(OrganizationContext);

  const teamIdsSet = useMemo(() => new Set(teamIds), [teamIds]);
  const locationIdsSet = useMemo(() => new Set(locationIds), [locationIds]);

  const teams = useMemo(() => {
    return teamList.filter(
      (team) => !team.locationId && teamIdsSet.has(team.teamId),
    );
  }, [teamList, teamIdsSet]);

  const areAllTeamsSelected = useMemo(
    () =>
      teamList
        .filter((team) => !team.locationId)
        .every((team) => teamIdsSet.has(team.teamId)),
    [teamList, teamIdsSet],
  );

  const userIdsSet = useMemo(() => new Set(userIds), [userIds]);

  const users = useMemo(() => {
    if (!personList || !Array.isArray(personList)) {
      return [];
    }
    return personList
      .filter((person) => userIdsSet.has(person.personId))
      .sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
  }, [personList, userIdsSet]);

  const areAllUsersSelected = useMemo(() => {
    if (!personList || !Array.isArray(personList)) {
      return false;
    }
    return personList.every((person) => userIdsSet.has(person.personId));
  }, [personList, userIdsSet]);

  const locations = useMemo(() => {
    return locationList.filter((location) => {
      return !!location.locationId && locationIdsSet.has(location.locationId);
    });
  }, [locationList, locationIdsSet]);

  const areAllLocationsSelected = useMemo(
    () =>
      locationList
        .filter((location) => !!location.locationId)
        .every((location) => locationIdsSet.has(location.locationId)),
    [locationList, locationIdsSet],
  );

  const isNothingSelected = useMemo(
    () => teams.length === 0 && locations.length === 0 && users.length === 0,
    [teams, locations, users],
  );

  const emptySelectedTeams = useMemo(() => {
    return (teamList || []).filter((team) => {
      const persons = personsByTeam[team.teamId] || [];
      return persons.length === 0;
    });
  }, [teamList, personsByTeam]);

  const emptySelectedLocations = useMemo(() => {
    return (locationList || []).filter((location) => {
      const persons = personsByLocation[location.locationId] || [];
      return persons.length === 0;
    });
  }, [locationList, personsByLocation]);

  const content = (
    <>
      {onPress && (
        <TooltipText tooltip="Click to override individual recipients">
          <ClickStyledText>Recipient Summary</ClickStyledText>
        </TooltipText>
      )}

      <ScrollView style={classes.scrollContainer}>
        {isNothingSelected ? (
          <Text style={[classes.content, classes.noneText]}>No recipients</Text>
        ) : (
          <>
            <Text style={classes.headers}>Teams:</Text>
            {teams.length > 0 && !areAllTeamsSelected ? (
              teams.map((team) => (
                <Text key={team.teamId} style={classes.content}>
                  {team.name}
                </Text>
              ))
            ) : (
              <>
                <Text style={[classes.content, classes.noneText]}>
                  {areAllTeamsSelected ? "All teams" : "No recipients"}
                </Text>
                {areAllTeamsSelected && emptySelectedTeams.length > 0 && (
                  <>
                    <Text style={[classes.content, classes.noneText]}>
                      Note: The following teams have no associated individuals
                    </Text>
                    {emptySelectedTeams.map((team) => (
                      <Text
                        key={team.teamId}
                        style={[classes.content, classes.noneText]}
                      >
                        - {team.name}
                      </Text>
                    ))}
                  </>
                )}
              </>
            )}
            <Text style={classes.headers}>Places:</Text>
            {locations.length > 0 && !areAllLocationsSelected ? (
              locations.map((location) => (
                <Text key={location.locationId} style={classes.content}>
                  {location.name}
                </Text>
              ))
            ) : (
              <>
                <Text style={[classes.content, classes.noneText]}>
                  {areAllLocationsSelected ? "All locations" : "No recipients"}
                </Text>
                {areAllLocationsSelected &&
                  emptySelectedLocations.length > 0 && (
                    <>
                      <Text style={[classes.content, classes.noneText]}>
                        Note: The following locations have no associated
                        individuals
                      </Text>
                      {emptySelectedLocations.map((location) => (
                        <Text
                          key={location.locationId}
                          style={[classes.content, classes.noneText]}
                        >
                          - {location.name}
                        </Text>
                      ))}
                    </>
                  )}
              </>
            )}

            <Text style={classes.headers}>Individuals:</Text>
            {users.length > 0 && !areAllUsersSelected ? (
              users.map((user) => (
                <Text key={user.personId} style={classes.content}>
                  {user.firstName} {user.lastName}
                </Text>
              ))
            ) : (
              <Text style={[classes.content, classes.noneText]}>
                {areAllUsersSelected ? "All individuals" : "No recipients"}
              </Text>
            )}
          </>
        )}
      </ScrollView>
    </>
  );

  return onPress ? (
    <PressableContainer onPress={onPress}>{content}</PressableContainer>
  ) : (
    <View>{content}</View>
  );
}
